import React, {useRef, useState} from "react";
import ProgressBar from "@ramonak/react-progress-bar";

/**
 * @enum {String}
 */

export const LoadingStatus = {
    IDLE: 'idle',
    START: 'start',
    RUNNING: 'running',
    COMPLETE: 'complete',
}

const useLoadingDialog = () =>
{
    /**
     * @typedef {Object} NotificationObject
     *
     * @property {LoadingStatus} status The status of the current transactions which shoul dbe one of 'idle', 'starting', 'running' or 'complete'
     * @property {number} pageCount Total number of objects which will be counted in the progress bar
     * @property {number} pagesLoaded Number of objects loaded so far ( to be used in the percentage calculation )
     * @property {number} lastPageLoadTimeSeconds Load time of the last step - used to calculate completion time
     * @property {number} totalLoadTimeSeconds Cumulative loading time
     *
     */

    /**
     * @type {NotificationObject}
     * @const
     */
    const INITIAL_LOADING_STATUS = {status: LoadingStatus.IDLE, pageCount: 0, pagesLoaded: 0, lastPageLoadTimeSeconds: 0, totalLoadTimeSeconds: 0} ;

    const [ loadingProgress, setLoadingProgress ] = useState ( INITIAL_LOADING_STATUS ) ;

    const lastLoadingFallback = useRef ( 1 );

    const getPcentCompleted = () =>
    {
        const pageCount =  Number ( loadingProgress.pageCount  ) ;
        const pagesLoaded =  Number( loadingProgress.pagesLoaded );

        const pc = pageCount === 0 ? 0 : Math.trunc( (pagesLoaded / pageCount ) * 100 );

        return pc ;
    }

    /**
     * Sets the current running status of the progress counter, including overall counts and progress so far
     *
     * @param {NotificationObject} statusObject The notification object containing the overall progress information
     *
     * @returns {string} The strong to be displayed, or 'Calculating'
     *
     * @example
     *
     * setProgress ( {status: 'running', pageCount:1, pagesLoaded: 0, lastPageLoadTimeSeconds: 3, totalLoadTimeSeconds: 5} )
     *
     */
    const setProgress = ( statusObject ) =>
    {
        setLoadingProgress( {...loadingProgress, ...statusObject });
    }

    /**
     * Returns the remaining time as a string which can be included in the calling dialog
     *
     * @returns {string} The strong to be displayed, or 'Calculating'
     *
     */
    const getRemainingTime = () =>
    {
        let useSeconds = 1 ;

        if ( loadingProgress.status === 'done')
        {
            return "Complete!" ;
        }
        else
        {
            const pageCount =  Number ( loadingProgress.pageCount  ) ;
            const pagesLoaded =  Number(loadingProgress.pagesLoaded);

            const lastPageLoadTimeSeconds = loadingProgress.lastPageLoadTimeSeconds ;

            if ( lastPageLoadTimeSeconds === 0 )
            {
                useSeconds = lastLoadingFallback.current ;
            }
            else
            {
                useSeconds = lastPageLoadTimeSeconds ;
            }

            lastLoadingFallback.current = useSeconds ;

            if ( pagesLoaded !== 0 && pageCount === pagesLoaded )
            {
                return '' ;
            }
            else if ( pageCount === 0  )
            {
                return "Calculating time..." ;
            }
            else
            {
                const secs = ( pageCount - pagesLoaded ) * useSeconds ;

                return "Estimated time remaining : " + secs + ' seconds' ;
            }

        }
    }

    const renderProgressBar = () =>
    {
        return (
            <ProgressBar completed={getPcentCompleted()} maxCompleted={100} />
        )
    }

    const isComplete = loadingProgress.status === LoadingStatus.COMPLETE ;

    return { setProgress, renderProgressBar, getRemainingTime, isComplete };
}

export default useLoadingDialog;